import { Loader2, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { NodeSelect } from "@/components/links/LinkDialog";
import type { HeadlineValueDto, NodeChangeDto } from "@/api/types";
import { useBase, useCompare, useScenarioDrivers, useScenarioSummary } from "@/hooks/world";
import { fmtValue, pct, points } from "@/lib/format";
import { fmtByDate, fmtMonth, GRID, idx, monthAt } from "@/lib/months";
import { headline, heroDate } from "@/lib/results";
import { toastUndo } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { useStore, useTerm } from "@/model/store";
import type { ModelNode, Lever, MetricNode, Scenario } from "@/model/types";
import { NodeIcon } from "@/components/NodeIcon";
import { ExactMonthHint } from "./NodeScenarioStrip";
import { biggestChange, leverSentence, scenarioChanceLabel, scenarioReason, scenariosTouching, type ScenarioFigures } from "./scenarioText";

export { ScenarioBar } from "./ScenarioBar";
export { NodeScenarioStrip } from "./NodeScenarioStrip";
export { biggestChange, leverSentence, scenarioReason, scenariosTouching } from "./scenarioText";

/** A shimmering placeholder for a number that's still being worked out. */
function Pending({ className }: { className?: string }) {
	return <span aria-label="Working it out" className={cn("inline-block h-4 w-12 animate-pulse rounded bg-muted align-middle", className)} />;
}

/** A headline value from the API as text: "28%" or "$2.33M". */
function valueText(v: HeadlineValueDto | null | undefined, unit: string | null): React.ReactNode {
	if (!v) return <Pending />;
	return v.kind === "event" ? pct(v.chance) : fmtValue(v.likely, unit ?? "");
}

/**
 * How far a node moved from Baseline, from the API's change: "+12 points" for events, "−$2.33M (−100%)"
 * for metrics (review E3). Red only for a metric that drops; everything else is neutral (review H5).
 */
function deltaLine(kind: "event" | "metric", unit: string | null, c: NodeChangeDto | null): { text: string; className: string } | null {
	if (!c || !c.moved) return null;
	if (kind === "event") return { text: points(c.delta), className: "text-muted-foreground" };
	const loss = c.delta < 0 ? "text-[var(--risk-ink)]" : "text-muted-foreground";
	// A rate like growth is already a percentage: "−2 points" reads better than "−2% (−2%)".
	if (unit === "%") return Math.abs(c.delta) >= 0.005 ? { text: points(c.delta), className: loss } : null;
	const sign = c.delta > 0 ? "+" : "−";
	const rel = c.relative === null ? "" : ` (${sign}${Math.round(Math.abs(c.relative) * 100)}%)`;
	return { text: `${sign}${fmtValue(Math.abs(c.delta), unit ?? "")}${rel}`, className: loss };
}

/** "Chance: 7%" or "Hypothetical", with what the chance is the chance of (review E2, 8.3). */
export function ScenarioBadge({ scenario }: { scenario: Scenario }) {
	const nodes = useStore((s) => s.world.nodes);
	const term = useTerm();
	const r = useScenarioSummary(scenario.id).data?.summary;
	if (!r || scenario.levers.length === 0) return null;
	const reason = scenarioReason(scenario, nodes);
	const label = r.impossible
		? "Can’t happen under the current forecasts"
		: r.hypothetical
			? "Hypothetical"
			: `${term("Chance of this scenario", "P(levers)")}: ${pct(r.probability)}`;
	const why = r.impossible
		? `Under the current forecasts, ${reason || "what it assumes"} can’t all happen.`
		: r.hypothetical
			? "It sets values or forces an outcome rather than only assuming one, so it has no chance of its own. It shows what would follow."
			: `The chance that ${reason}, under the current forecasts. A low chance is fine for a planning scenario: it shows what to prepare for.`;
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Badge variant="outline" tabIndex={0} className="cursor-help font-normal">
					{label}
				</Badge>
			</TooltipTrigger>
			<TooltipContent className="max-w-72">{why}</TooltipContent>
		</Tooltip>
	);
}

/** "Sets 1 node. Biggest change: Layoffs needed 28% → 100%" (review 8.3). */
export function ScenarioSummary({ scenario, className }: { scenario: Scenario; className?: string }) {
	const nodes = useStore((s) => s.world.nodes);
	const changes = useScenarioSummary(scenario.id).data?.summary.changes ?? null;
	const n = scenario.levers.length;
	if (n === 0) return <p className={cn("text-sm text-muted-foreground", className)}>Nothing set yet.</p>;
	const change = changes ? biggestChange(nodes, changes) : null;
	return (
		<p className={cn("text-sm text-muted-foreground", className)}>
			Sets {n} {n === 1 ? "node" : "nodes"}.{" "}
			{change ? (
				<>
					Biggest change: <span className="text-foreground">{change.node.title}</span> {change.from} →{" "}
					<span className="font-medium text-foreground">{change.to}</span>
				</>
			) : changes ? (
				"Nothing else moves by more than a point."
			) : (
				<Pending className="w-40" />
			)}
		</p>
	);
}

/** Baseline vs this scenario for every node it sets or changes (the API's "What changes", biggest first). */
export function CompareTable({ scenario }: { scenario: Scenario }) {
	const nodes = useStore((s) => s.world.nodes);
	const term = useTerm();
	const { data, pending } = useScenarioSummary(scenario.id);
	const r = data?.summary;
	// The nodes it sets first, then what moved.
	const rows = (r?.changes ?? []).filter((c) => nodes[c.handle]).sort((a, b) => Number(b.set_here) - Number(a.set_here));

	return (
		<div className="overflow-x-auto rounded-xl border bg-card">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b text-left align-bottom">
						<th className="px-4 py-3 font-medium">
							<div className="flex items-center gap-2">
								What changes
								{pending && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
							</div>
						</th>
						<th className="px-4 py-3 text-right font-medium">Baseline</th>
						<th className="px-4 py-3 text-right font-medium">
							{/* Scenario amber is too light for text: a swatch carries the colour. */}
							<span className="mr-1.5 inline-block size-2 rounded-full bg-[var(--scenario)]" aria-hidden />
							{scenario.name}
						</th>
					</tr>
				</thead>
				<tbody className="tabular-nums">
					<tr className="border-b bg-muted/40 text-xs text-muted-foreground">
						<td className="px-4 py-2">{term("How likely is this scenario?", "P(levers)")}</td>
						<td className="px-4 py-2 text-right">–</td>
						<td className="px-4 py-2 text-right">{!r ? <Pending /> : chanceWithReason(scenario, r, nodes)}</td>
					</tr>
					{r && rows.length === 0 && (
						<tr>
							<td colSpan={3} className="px-4 py-4 text-muted-foreground">
								{scenario.levers.length ? "Nothing moves by more than a point." : "Nothing set yet."}
							</td>
						</tr>
					)}
					{rows.map((c) => {
						const n = nodes[c.handle];
						const delta = deltaLine(c.kind, c.unit, c);
						const f = (v: number) => (c.kind === "event" ? pct(v) : fmtValue(v, c.unit ?? ""));
						return (
							<tr key={c.handle} className="border-b last:border-0">
								<td className="px-4 py-2.5">
									<Link to={`/node/${n.id}`} className="hover:underline">
										<NodeIcon node={n} className="mr-1.5 text-muted-foreground" />
										{n.title}
									</Link>
									<div className="text-xs text-muted-foreground">
										{n.kind === "event" ? `chance by ${fmtByDate(c.date)}` : `likely, ${fmtByDate(c.date)}`}
										{c.set_here && <span className="ml-2 text-[var(--scenario-ink)]">set in this scenario</span>}
									</div>
								</td>
								<td className="px-4 py-2.5 text-right">{f(c.baseline)}</td>
								<td className="px-4 py-2.5 text-right">
									<div className="font-medium">{f(c.scenario)}</div>
									{delta && <div className={cn("text-xs", delta.className)}>{delta.text}</div>}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

/** "1%: Acme leaves in exactly Mar 2027 and US recession by Dec 2027" (review E2). */
function chanceWithReason(scenario: Scenario, r: ScenarioFigures, nodes: Record<string, ModelNode>): string {
	const label = scenarioChanceLabel(scenario, r);
	const reason = r.probability !== null && !r.hypothetical && !r.impossible ? scenarioReason(scenario, nodes) : "";
	return reason ? `${label}: ${reason}` : label;
}

export function ScenarioEditor({ scenario }: { scenario: Scenario }) {
	const nodes = useStore((s) => s.world.nodes);
	const deleteScenario = useStore((s) => s.deleteScenario);
	const upsert = useStore((s) => s.upsertLever);
	const remove = useStore((s) => s.removeLever);
	const setActive = useStore((s) => s.setActiveScenario);
	const navigate = useNavigate();
	// Calculated nodes follow their drivers, so they can't be set directly.
	const settable = Object.values(nodes).filter((n) => !(n.kind === "event" && n.definition) && !(n.kind === "metric" && n.formula));
	const free = settable.filter((n) => !scenario.levers.some((l) => l.node === n.id));

	/** Start from the baseline so the scenario's values can be dragged from there. */
	function setValues(id: string) {
		const n = nodes[id];
		const lever: Lever =
			n.kind === "event" ? { id, kind: "curve", node: id, by: { ...n.by } } : { id, kind: "range", node: id, range: structuredClone(n.range) };
		void upsert(scenario.id, lever);
		setActive(scenario.id);
		navigate(`/node/${id}`);
	}
	function assume(id: string) {
		void upsert(scenario.id, { id, kind: "event", node: id, happens: "true", mode: "assume" });
	}

	return (
		<div className="rounded-xl border border-[var(--scenario)]/50 bg-card">
			<div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
				<h2 className="text-sm font-semibold">What it sets</h2>
				<Button
					size="sm"
					variant="ghost"
					className="ml-auto"
					onClick={() => {
						void deleteScenario(scenario.id).then((ok) => {
							if (!ok) return;
							toastUndo(`Deleted scenario “${scenario.name}”`);
							navigate("/");
						});
					}}
				>
					<Trash2 className="size-4" /> Delete scenario
				</Button>
			</div>
			<div className="divide-y">
				{scenario.levers.length === 0 && (
					<p className="px-4 py-4 text-sm text-muted-foreground">
						Nothing set yet. Open any event or metric with this scenario selected and drag its chart, just like the baseline, or assume an event happens below.
					</p>
				)}
				{scenario.levers.map((l) => (
					<LeverRow
						key={l.id}
						lever={l}
						node={nodes[l.node]}
						onChange={(next) => void upsert(scenario.id, next)}
						onRemove={() => void remove(scenario.id, l.node)}
						onEdit={() => {
							setActive(scenario.id);
							navigate(`/node/${l.node}`);
						}}
					/>
				))}
			</div>
			{free.length > 0 && (
				<div className="flex flex-wrap items-center gap-3 border-t bg-muted/30 px-4 py-3 text-sm">
					<div className="w-64">
						<NodeSelect value={undefined} nodes={free} placeholder="Set values for…" onChange={setValues} />
					</div>
					<div className="w-64">
						<NodeSelect value={undefined} nodes={free.filter((n) => n.kind === "event")} placeholder="Assume an event happens…" onChange={assume} />
					</div>
				</div>
			)}
		</div>
	);
}

/** Holds a slider's value on screen and commits it 250 ms after the last change (review K2). */
function useDebouncedCommit<T>(commit: (v: T) => void, ms = 250) {
	const [live, setLive] = useState<T | null>(null);
	const timer = useRef<number | undefined>(undefined);
	const latest = useRef(commit);
	useEffect(() => {
		latest.current = commit;
	});
	const queued = useRef<{ v: T } | null>(null);
	const flush = useCallback(() => {
		window.clearTimeout(timer.current);
		const q = queued.current;
		queued.current = null;
		if (q) {
			latest.current(q.v);
			setLive(null);
		}
	}, []);
	// Don't lose a pending commit when the row goes away.
	useEffect(() => flush, [flush]);
	const schedule = useCallback(
		(v: T) => {
			setLive(v);
			queued.current = { v };
			window.clearTimeout(timer.current);
			timer.current = window.setTimeout(flush, ms);
		},
		[flush, ms],
	);
	return { live, setLive, schedule };
}

function MonthSlider({ value, label = "Month", onCommit }: { value: string; label?: string; onCommit: (m: string) => void }) {
	const { live, setLive, schedule } = useDebouncedCommit<number>((v) => onCommit(monthAt(v)));
	const t = live ?? Math.max(0, idx(value));
	return (
		<div className="flex min-w-56 flex-1 items-center gap-3">
			<Slider
				value={[t]}
				min={0}
				max={Math.min(GRID.length - 1, idx("2030-12"))}
				step={1}
				onValueChange={([v]) => setLive(v)}
				onValueCommit={([v]) => schedule(v)}
				aria-label={label}
			/>
			<span className="w-20 shrink-0 text-right tabular-nums">{fmtMonth(monthAt(t))}</span>
		</div>
	);
}

export function LeverRow({
	lever,
	node,
	onChange,
	onRemove,
	onEdit,
}: {
	lever: Lever;
	node: ModelNode;
	onChange: (l: Lever) => void;
	onRemove: () => void;
	onEdit: () => void;
}) {
	const term = useTerm();
	if (!node) return null;
	return (
		<div className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
			<span className="w-48 shrink-0 font-medium">
				<NodeIcon node={node} className="mr-1.5 text-muted-foreground" />
				{node.title}
			</span>
			{lever.kind === "event" && (
				<>
					<Select value={lever.happens} onValueChange={(v) => onChange({ ...lever, happens: v as typeof lever.happens, month: lever.month ?? "2027-06" })}>
						<SelectTrigger size="sm" className="w-44" aria-label={`What ${node.title} does in this scenario`}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="true">happens (some time)</SelectItem>
							<SelectItem value="by">happens by…</SelectItem>
							<SelectItem value="at">happens in exactly…</SelectItem>
							<SelectItem value="false">doesn’t happen</SelectItem>
						</SelectContent>
					</Select>
					{(lever.happens === "at" || lever.happens === "by") && (
						<MonthSlider value={lever.month ?? "2027-06"} label={`Month ${node.title} happens`} onCommit={(month) => onChange({ ...lever, month })} />
					)}
					<ToggleGroup
						type="single"
						size="sm"
						variant="outline"
						value={lever.mode}
						onValueChange={(v) => v && onChange({ ...lever, mode: v as "assume" | "make" })}
						className="ml-auto"
					>
						<ToggleGroupItem value="assume" title="Suppose this turns out true: what probably caused it updates too">
							{term("Assume", "conditioning")}
						</ToggleGroupItem>
						<ToggleGroupItem value="make" title="Force this: only what’s downstream changes">
							{term("Make it so", "do()")}
						</ToggleGroupItem>
					</ToggleGroup>
				</>
			)}
			{(lever.kind === "curve" || lever.kind === "range") && (
				<>
					<span className="text-muted-foreground">{leverSentence(lever, { [node.id]: node })}</span>
					<Button size="sm" variant="outline" className="ml-auto" onClick={onEdit}>
						Edit values
					</Button>
				</>
			)}
			<Button size="icon" variant="ghost" className="size-8" onClick={onRemove} aria-label="Remove lever">
				<X className="size-4" />
			</Button>
			{lever.kind === "event" && <ExactMonthHint lever={lever} onChange={onChange} className="basis-full sm:pl-51" />}
		</div>
	);
}

export function DroveTheDifference({ scenario }: { scenario: Scenario }) {
	const nodes = useStore((s) => s.world.nodes);
	const term = useTerm();
	const changes = useScenarioSummary(scenario.id).data?.summary.changes ?? [];
	const [picked, setPicked] = useState<string | null>(null);
	// What the reader wants explained is the biggest knock-on effect, not a node the scenario sets itself (review E4).
	const moved = changes.filter((c) => c.moved && nodes[c.handle]);
	const knockOn = moved.filter((c) => !c.set_here);
	// Events first, as in biggestChange: a metric swing is often just the lever's own effect (Acme revenue → $0).
	const target = picked ?? (knockOn.find((c) => c.kind === "event") ?? knockOn[0] ?? moved[0])?.handle ?? scenario.levers[0]?.node ?? Object.keys(nodes)[0];
	const node = nodes[target];
	// "What drove the difference" is the API's attribution (Shapley up to 8 levers).
	const { data, pending } = useScenarioDrivers(node && scenario.levers.length ? scenario.id : null, node ? target : null);
	if (!node) return null;
	const date = data?.date ?? heroDate(node);
	const b = data ? data.baseline : null;
	const s = data ? data.scenario_value : null;
	const unit = node.kind === "metric" ? (node as MetricNode).unit : "";
	const fmt = (v: number) => (node.kind === "event" ? points(v) : `${v >= 0 ? "+" : "−"}${fmtValue(Math.abs(v), unit)}`);
	const items = data?.contributions ?? [];
	const max = Math.max(...items.map((d) => Math.abs(d.delta)), 1e-9);

	return (
		<div className="rounded-xl border bg-card p-4">
			<div className="mb-3 flex flex-wrap items-center gap-2">
				<h3 className="text-sm font-semibold">{term("What drove the difference", "attribution")}</h3>
				{pending && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
				<div className="ml-auto w-60">
					<NodeSelect value={target} onChange={setPicked} nodes={Object.values(nodes)} />
				</div>
			</div>
			{scenario.levers.length === 0 ? (
				<p className="text-sm text-muted-foreground">Set something in this scenario to see what it changes.</p>
			) : (
				<>
					{b !== null && s !== null && (
						<p className="mb-3 font-serif text-[15px]">
							{node.kind === "event"
								? `Chance of ${node.title.toLowerCase()} by ${fmtByDate(date)}: ${pct(b)} in Baseline, ${pct(s)} here (${points(s - b)}).`
								: `${node.title} in ${fmtByDate(date)}: likely ${fmtValue(b, unit)} in Baseline, ${fmtValue(s, unit)} here.`}
						</p>
					)}
					<ul className="space-y-2">
						{items.map((d) => {
							const l = scenario.levers.find((x) => x.node === d.node);
							return (
								<li key={d.node} className="grid grid-cols-[minmax(0,14rem)_1fr_5.5rem] items-center gap-3 text-sm">
									<span className="truncate">{l ? leverSentence(l, nodes) : d.title}</span>
									<div className="relative h-2.5 rounded-full bg-muted">
										<div className="absolute top-0 left-1/2 h-2.5 w-px bg-foreground/30" />
										<div
											// Red only when a metric drops (a loss); other directions are neutral (review H5).
											className={cn("absolute top-0 h-2.5 rounded-full", node.kind === "metric" && d.delta < 0 ? "bg-[var(--risk)]/80" : "bg-foreground/45")}
											style={d.delta >= 0 ? { left: "50%", width: `${(d.delta / max) * 50}%` } : { right: "50%", width: `${(-d.delta / max) * 50}%` }}
										/>
									</div>
									<span className="text-right tabular-nums">{fmt(d.delta)}</span>
								</li>
							);
						})}
					</ul>
				</>
			)}
		</div>
	);
}

/** Every scenario side by side for a set of nodes (e.g. a node and everything that feeds it), from `GET /compare`. */
export function ScenarioMatrix({ ids, focus, all }: { ids: string[]; focus?: string; all?: boolean }) {
	const nodes = useStore((s) => s.world.nodes);
	const everyScenario = useStore((s) => s.world.scenarios);
	const active = useStore((s) => s.activeScenario);
	// Only scenarios that set something among these nodes (plus the active one).
	const touching = all ? everyScenario : scenariosTouching(everyScenario, ids);
	const scenarios = everyScenario.filter((sc) => touching.includes(sc) || sc.id === active);
	const setActive = useStore((s) => s.setActiveScenario);
	const term = useTerm();
	const { data, pending } = useCompare(focus ?? null);
	// Baseline's run is usually loaded already: show its column at once while the scenarios compute (review E5).
	const base = useBase().data;
	const baselineNow = (n: ModelNode): HeadlineValueDto | null => {
		if (!base) return null;
		const date = heroDate(n);
		const v = headline(n, base, date);
		if (v === null) return null;
		return n.kind === "event" ? { kind: "event", date, chance: v } : { kind: "metric", date, low: v, likely: v, high: v };
	};
	const col = (id: string | null) => data?.columns.findIndex((c) => c.id === id) ?? -1;
	const rowOf = new Map((data?.rows ?? []).map((r) => [r.handle, r]));
	const rows = ids.map((id) => nodes[id]).filter(Boolean);

	return (
		<div className="overflow-x-auto rounded-xl border bg-card">
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b text-left align-bottom">
						<th className="sticky left-0 bg-card px-4 py-3 font-medium">
							<div className="flex items-center gap-2">
								Compare
								{pending && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
							</div>
						</th>
						<th className="px-4 py-3 text-right font-medium">Baseline</th>
						{scenarios.map((s) => (
							<th key={s.id} className="px-4 py-3 text-right font-normal">
								<button
									onClick={() => setActive(s.id)}
									className={cn("text-right hover:underline", active === s.id && "font-semibold text-[var(--scenario-ink)]")}
								>
									{s.name}
								</button>
							</th>
						))}
					</tr>
				</thead>
				<tbody className="tabular-nums">
					<tr className="border-b bg-muted/40 text-xs text-muted-foreground">
						<td className="sticky left-0 bg-muted/40 px-4 py-2">{term("How likely is this scenario?", "P(levers)")}</td>
						<td className="px-4 py-2 text-right">–</td>
						{scenarios.map((s) => {
							const c = data?.columns[col(s.id)];
							return (
								<td key={s.id} className="px-4 py-2 text-right">
									{!c ? <Pending /> : <span title={chanceWithReason(s, c, nodes)}>{scenarioChanceLabel(s, c)}</span>}
								</td>
							);
						})}
					</tr>
					{scenarios.length === 0 && (
						<tr>
							<td colSpan={2} className="px-4 py-3 text-sm text-muted-foreground">
								No scenario sets anything here yet. Use “+ Scenario” above to create one or add an existing one.
							</td>
						</tr>
					)}
					{rows.map((n) => {
						const row = rowOf.get(n.id);
						const unit = n.kind === "metric" ? (n as MetricNode).unit : null;
						const date = row?.date ?? heroDate(n);
						return (
							<tr key={n.id} className={cn("border-b last:border-0", n.id === focus && "bg-muted/30")}>
								<td className="sticky left-0 bg-card px-4 py-2.5">
									<Link to={`/node/${n.id}`} className={cn("hover:underline", n.id === focus && "font-semibold")}>
										<NodeIcon node={n} className="mr-1.5 text-muted-foreground" />
										{n.title}
									</Link>
									<div className="text-xs text-muted-foreground">{n.kind === "event" ? `chance by ${fmtByDate(date)}` : `likely, ${fmtByDate(date)}`}</div>
								</td>
								<td className="px-4 py-2.5 text-right font-medium">{valueText(row?.cells[col(null)]?.value ?? baselineNow(n), unit)}</td>
								{scenarios.map((s) => {
									const cell = row?.cells[col(s.id)];
									const delta = cell ? deltaLine(n.kind, unit, cell.change) : null;
									return (
										<td key={s.id} className={cn("px-4 py-2.5 text-right", active === s.id && "bg-[var(--scenario)]/5")}>
											<div className={cn(delta ? "font-medium" : "text-muted-foreground")}>{valueText(cell?.value, unit)}</div>
											{cell?.set_here ? (
												<div className="text-xs text-[var(--scenario-ink)]">set here</div>
											) : (
												delta && <div className={cn("text-xs whitespace-nowrap", delta.className)}>{delta.text}</div>
											)}
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
